/**
 * Thin fetch wrapper used by pages that want Bearer auth without
 * importing AuthContext. Note several sharp edges callers must know:
 *   - On 401 it clears tokens and hard-navigates to '/'. It still
 *     returns the (non-ok) Response so callers see `res.ok === false`
 *     and fail gracefully rather than throwing a TypeError on
 *     `.json()` mid-redirect.
 *   - 'Content-Type: application/json' is sent by default — EXCEPT
 *     when `options.body` is a FormData, where the header is omitted
 *     entirely so the browser can set the multipart boundary itself.
 *     Caller-supplied headers always win.
 *   - The stored refresh_token is never exchanged — 401 means "session
 *     over", even if a valid refresh token exists.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Wrapper around fetch that automatically adds the Authorization header
 * using the access token stored in localStorage.
 *
 * Usage:
 *   import { apiFetch } from '../utils/api';
 *   const data = await apiFetch('/api/surgeons');
 */
export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('access_token');

  // Default to JSON, but FormData must NOT get a Content-Type — the
  // browser has to set the multipart boundary itself. Caller-supplied
  // headers override the default either way.
  const headers = { ...(options.headers || {}) };
  if (!(options.body instanceof FormData) && !('Content-Type' in headers)) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BACKEND_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Token expired or invalid — clear storage and reload to login page.
    // Still return the Response so callers can check res.ok rather than
    // crashing on .json() of undefined mid-navigation.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/';
  }

  return res;
}

/**
 * Convenience helpers
 */
export const apiGet  = (path, opts = {}) => apiFetch(path, { ...opts, method: 'GET'  });
export const apiPost = (path, body, opts = {}) =>
  apiFetch(path, { ...opts, method: 'POST',  body: JSON.stringify(body) });
export const apiPut  = (path, body, opts = {}) =>
  apiFetch(path, { ...opts, method: 'PUT',   body: JSON.stringify(body) });
export const apiDel  = (path, opts = {}) => apiFetch(path, { ...opts, method: 'DELETE' });
